import { isDefined } from "@clipboard-health/util-ts";
import { Box, CircularProgress, Dialog, DialogContent, Stack } from "@mui/material";
import { useEffectivePermissions } from "@src/appV2/Auth/hooks/useEffectivePermissions";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { type Facility } from "@src/appV2/User/api/useSearchFacilities";
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

import { type WorkerShift } from "./api/useGetWorkerShifts";
import { type WorkerStripeAccount } from "./api/useGetWorkerStripeAccount";
import { type WorkplaceShift } from "./api/useGetWorkplaceShifts";
import { type ShiftSearchResult } from "./api/useSearchShift";
import { KeyboardHints } from "./components/KeyboardHints";
import { OmnisearchEmptyState } from "./components/OmnisearchEmptyState";
import { OmnisearchInput } from "./components/OmnisearchInput";
import { type ShiftAction } from "./components/ShiftActionItem";
import { useOmnisearchKeyboard } from "./hooks/useOmnisearchKeyboard";
import { useOmnisearchResults } from "./hooks/useOmnisearchResults";
import { useOmnisearchSelect } from "./hooks/useOmnisearchSelect";
import { OmnisearchResultsList } from "./OmnisearchResultsList";
import { type AgentImpersonateData } from "./types";

interface OmnisearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectAgent: (agent: Agent) => void;
  onSelectFacility: (facility: Facility) => void;
  onSelectShift: (shift: ShiftSearchResult) => void;
  onSelectShiftAction: (shift: ShiftSearchResult, action: ShiftAction) => void;
  onSelectAgentShift: (workerShift: WorkerShift) => void;
  onSelectAgentStripe: (stripeAccount: WorkerStripeAccount) => void;
  onSelectAgentImpersonate: (data: AgentImpersonateData) => boolean;
  onSelectWorkplaceShift: (workplaceShift: WorkplaceShift) => void;
}

export function OmnisearchDialog(props: OmnisearchDialogProps) {
  const {
    open,
    onClose,
    onSelectAgent,
    onSelectFacility,
    onSelectShift,
    onSelectShiftAction,
    onSelectAgentShift,
    onSelectAgentStripe,
    onSelectAgentImpersonate,
    onSelectWorkplaceShift,
  } = props;
  const [searchInput, setSearchInput] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expandedShiftId, setExpandedShiftId] = useState<string | null>(null);
  const [expandedWorkerId, setExpandedWorkerId] = useState<string | null>(null);
  const [expandedWorkplaceId, setExpandedWorkplaceId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousShiftIdRef = useRef<string | null>(null);

  const { canImpersonateUsers } = useEffectivePermissions();

  const {
    agents,
    facilities,
    shift,
    shiftData,
    workerShifts,
    workerStripeAccount,
    workplaceShifts,
    allResults,
    isLoading,
    isLoadingWorkerShifts,
    isLoadingWorkerStripeAccount,
    workerHasShifts,
    workerHasStripeAccount,
    isLoadingWorkplaceShifts,
    workplaceHasShifts,
    hasResults,
    hasSearchInput,
    debouncedSearchInput,
  } = useOmnisearchResults({
    searchInput,
    expandedShiftId,
    expandedWorkerId,
    expandedWorkplaceId,
  });

  const resetState = useCallback(() => {
    setSearchInput("");
    setSelectedIndex(0);
    setExpandedShiftId(null);
    setExpandedWorkerId(null);
    setExpandedWorkplaceId(null);
    previousShiftIdRef.current = null;
  }, []);

  const handleSelect = useOmnisearchSelect({
    onSelectAgent,
    onSelectFacility,
    onSelectShift,
    onSelectShiftAction,
    onSelectAgentShift,
    onSelectAgentStripe,
    onSelectAgentImpersonate,
    onSelectWorkplaceShift,
    onClose,
    resetState,
  });

  const { handleKeyDown, copiedItem } = useOmnisearchKeyboard({
    allResults,
    selectedIndex,
    setSelectedIndex,
    setExpandedShiftId,
    setExpandedWorkerId,
    setExpandedWorkplaceId,
    onSelect: handleSelect,
  });

  useEffect(() => {
    setSelectedIndex(0);
    setExpandedWorkerId(null);
    setExpandedWorkplaceId(null);
  }, [debouncedSearchInput]);

  useEffect(() => {
    if (isDefined(shiftData) && shiftData.id !== previousShiftIdRef.current) {
      previousShiftIdRef.current = shiftData.id;
      setExpandedShiftId(shiftData.id);
    }
  }, [shiftData]);

  const handleClose = useCallback(() => {
    onClose();
    resetState();
  }, [onClose, resetState]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  return (
    <BerlinThemeProvider>
      <Dialog
        fullWidth
        maxWidth={false}
        open={open}
        PaperProps={{
          sx: (theme) => ({
            position: "fixed",
            top: "20%",
            margin: "0",
            width: "min(720px, calc(100vw - 32px))",
            borderRadius: theme.borderRadius?.large,
            boxShadow: theme.shadow?.modal,
            maxHeight: "76vh",
            overflow: "hidden",
          }),
        }}
        onClose={handleClose}
      >
        <DialogContent sx={{ padding: 2 }}>
          <OmnisearchInput
            inputRef={inputRef}
            value={searchInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          {hasSearchInput && (
            <Box sx={{ borderTop: 1, borderColor: "divider" }}>
              {isLoading && (
                <Stack sx={{ alignItems: "center", justifyContent: "center", paddingY: 6 }}>
                  <CircularProgress size={24} />
                </Stack>
              )}
              {!isLoading && !hasResults && (
                <OmnisearchEmptyState query={debouncedSearchInput} variant="noResults" />
              )}
              {!isLoading && hasResults && (
                <>
                  <Box sx={{ maxHeight: "50vh", overflowY: "auto" }}>
                    <OmnisearchResultsList
                      agents={agents}
                      copiedItem={copiedItem}
                      expandedShiftId={expandedShiftId}
                      expandedWorkerId={expandedWorkerId}
                      expandedWorkplaceId={expandedWorkplaceId}
                      facilities={facilities}
                      isLoadingWorkerShifts={isLoadingWorkerShifts}
                      isLoadingWorkerStripeAccount={isLoadingWorkerStripeAccount}
                      isLoadingWorkplaceShifts={isLoadingWorkplaceShifts}
                      selectedIndex={selectedIndex}
                      shift={shift}
                      workerHasShifts={workerHasShifts}
                      workerHasStripeAccount={workerHasStripeAccount}
                      workerImpersonation={{
                        canImpersonate: canImpersonateUsers,
                        disabledReason: !canImpersonateUsers
                          ? "You don't have permission to impersonate users."
                          : undefined,
                      }}
                      workerShifts={workerShifts}
                      workerStripeAccount={workerStripeAccount}
                      workplaceHasShifts={workplaceHasShifts}
                      workplaceShifts={workplaceShifts}
                      onSelect={handleSelect}
                    />
                  </Box>
                  <Box
                    sx={{
                      paddingY: 3,
                      paddingX: 4,
                      borderTop: 1,
                      borderColor: "divider",
                      display: "flex",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      gap: 3,
                    }}
                  >
                    <KeyboardHints />
                  </Box>
                </>
              )}
            </Box>
          )}
          {!hasSearchInput && <OmnisearchEmptyState variant="initial" />}
        </DialogContent>
      </Dialog>
    </BerlinThemeProvider>
  );
}
